import React from 'react'
import 'lib/hotnessNotifier'
import { DataProvider } from 'data/DataProvider'
import { logErrorBoundActivation } from 'lib/sentry'
import { ErrorPage } from 'common/ErrorPage'
import { LocaleProvider } from 'bundles/core/LocaleProvider'
import { ModalLayer } from 'common/modals/ModalLayer'
import ReactDOM from 'react-dom'
import { Map } from 'immutable'
import { softError } from 'lib/softError'
import { MemoryRouter } from 'react-router-dom'

// This is a slightly-more-efficient way to use functionality that relies on the
// rwgps Core infrastructure (DataProvider, LocaleProvider, MetricUnitsContext)
// within Recumbent. It bootstraps a single core-ish instance, and then renders
// recumbent components "inside" this instance using Portals.
//
// The primary features of Coreshim are access to APIs like showModal, DataProvider
// and connect.
//
// The primary gotcha is that, while it provides Core-like ameneties, the
// fundamental page structure of non-core pages doesn't permit SPA navigation,
// so it's not 1:1 compatible with core and as such we do NOT render a
// CoreContext.Provider as part of the CoreShim stack.
export class CoreShim extends React.PureComponent {
  state = {
    error: null,
    errorInfo: null,
    sentryEventId: null,
    renderedElements: Map()
  }

  componentDidCatch (error, errorInfo) {
    this.setState({ error, errorInfo })
    // see https://sentry.io/ridewithgpscom/rwgps-ui/getting-started/javascript-react/
    // for a full explanation
    logErrorBoundActivation(error, errorInfo)
      .then(sentryEventId => this.setState({ sentryEventId }))
  }

  renderWithinCoreShim = (id, elem) => {
    const { renderedElements } = this.state
    const container = document.getElementById(id)
    if (!container) {
      softError(`id '${id}' for CoreShim portal not found`)
      return
    }
    container.innerHTML = ''
    const portal = ReactDOM.createPortal(elem, container)
    this.setState({ renderedElements: renderedElements.set(id, portal) })
  }

  unmountWithinCoreShim = (id, elem) => {
    const { renderedElements } = this.state
    this.setState({ renderedElements: renderedElements.delete(id) })
  }

  render () {
    const { children } = this.props
    const { renderedElements, error, errorInfo, sentryEventId } = this.state

    return (
      <MemoryRouter>
        {error
          ? <ErrorPage error={error} errorInfo={errorInfo} sentryEventId={sentryEventId} />
          : <DataProvider>
            <LocaleProvider>
              {renderedElements.valueSeq()}
              {children}
              <ModalLayer />
            </LocaleProvider>
          </DataProvider>}
      </MemoryRouter>
    )
  }
}

export const bootCoreShim = () => {
  const div = document.createElement('div')
  div.id = 'coreshim'
  document.body.appendChild(div)
  const coreShimInstance = ReactDOM.render(<CoreShim />, div)
  return coreShimInstance
}
